import type { MouseEvent, ReactNode } from 'react';
import { buildWhatsAppUrl, formatPhone, phoneHref } from '@peles/shared';
import { WhatsAppIcon } from './WhatsAppIcon';

/** Rows that open on a press must not open when a link inside them is pressed. */
const stop = (e: MouseEvent) => e.stopPropagation();

export interface WhatsAppLinkProps {
  phone: string | null | undefined;
  /** Who the number is, for the button's accessible name ("WhatsApp Dana Cohen"). */
  name?: string;
  /** Icon size classes. */
  iconClassName?: string;
  className?: string;
}

/**
 * A small WhatsApp shortcut for a number: opens a chat with it (`wa.me`), in a
 * new tab. Renders nothing for a number that cannot be dialled. The icon stays
 * small; on a phone its press area is the full 44px (`min-h-11 min-w-11`) —
 * real space, not a margin trick, so it never reaches over a neighbouring
 * control — and compact from `sm` up.
 */
export function WhatsAppLink({
  phone,
  name,
  iconClassName = 'h-4 w-4',
  className = '',
}: WhatsAppLinkProps) {
  const url = buildWhatsAppUrl(phone);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stop}
      aria-label={`WhatsApp ${name ?? formatPhone(phone)}`}
      title="Message on WhatsApp"
      className={`inline-flex shrink-0 items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 text-green-600 hover:text-green-700 ${className}`}
    >
      <WhatsAppIcon className={iconClassName} />
    </a>
  );
}

export interface PhoneLinkProps {
  phone: string | null | undefined;
  /** Who the number is, for the WhatsApp shortcut's accessible name. */
  name?: string;
  /** Shown before the number inside the link (a phone icon). */
  icon?: ReactNode;
  /** Classes for the number's link. */
  className?: string;
  /** Classes for the wrapper holding the number and the shortcut. */
  wrapperClassName?: string;
  /** Show the WhatsApp shortcut beside the number (default on). */
  whatsapp?: boolean;
  whatsappIconClassName?: string;
}

/**
 * A phone number as it is shown everywhere: laid out (`formatPhone`), a
 * `tel:` link that calls it, and a small WhatsApp shortcut beside it. Both are
 * at least 44px tall on a phone.
 * Renders nothing when there is no number.
 */
export function PhoneLink({
  phone,
  name,
  icon,
  className = 'text-primary-600 hover:text-primary-700',
  wrapperClassName = '',
  whatsapp = true,
  whatsappIconClassName,
}: PhoneLinkProps) {
  const shown = formatPhone(phone);
  if (!phone || !shown) return null;
  return (
    <span className={`inline-flex items-center gap-2 ${wrapperClassName}`}>
      <a
        href={phoneHref('tel', phone)}
        onClick={stop}
        className={`inline-flex items-center gap-1 min-h-11 sm:min-h-0 ${className}`}
      >
        {icon}
        {shown}
      </a>
      {whatsapp && <WhatsAppLink phone={phone} name={name} iconClassName={whatsappIconClassName} />}
    </span>
  );
}
